import { TUserDataCamel } from '@/types';
import { getObjectValueByPath } from '@/utils/getObjectValueByPath';

/**
 * Checks if a field value is considered filled (non-empty).
 * A field is considered filled if:
 * - It's not undefined or null
 * - It's not equal to the path string (which indicates a missing nested property)
 * - For arrays, it has at least one element
 * - For strings, it's not an empty string
 * @param value - The value to check. Can be any type including undefined, null, arrays, or strings.
 * @param path - Optional dot-notation path string. If value equals path, it indicates the field was not found.
 * @returns true if the field is filled, false otherwise.
 */
const isFieldFilled = (value: unknown, path?: string) => {
  if (value === undefined || value === null) return false;
  if (value === path) return false;
  if (Array.isArray(value)) return value.length > 0;
  return value !== '';
};

/**
 * Checks if all required fields in a user data object are filled.
 * Uses dot-notation paths (e.g., 'userLocation.city', 'phone.number') to access nested properties.
 * @param fields - Array of field paths in dot-notation format (e.g., ['firstName', 'userLocation.city', 'phone.number']).
 * @param data - User data object to validate against.
 * @returns true if all specified fields are filled, false if any field is missing or empty.
 */
export const areFieldsFilled = (fields: string[], data: TUserDataCamel) =>
  fields.every((path) => isFieldFilled(getObjectValueByPath(data, path), path));
